import React from 'react';
import { Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

import WithErrorBoundary from '../../HOCs/WithErrorBoundary';

import { TBX_PLAYER_ENVS } from './constants';
import * as hooks from './hooks';
import reducer, { INITIAL_STATE } from './reducer';

import './styles.scss';
import LoadingSpinner from '../LoadingSpinner'

function Player({
  content,
  changeContent,
  eventListenerAccion = true,
  options,
  startCastingCallback,
  updatePositionCallback,
  onClickBackButton,
  translationTexts,
  playerHandleError,
  playerCallback,
  fullWindow = true,
  enableGoBackOnCompletion = false,
  LoadingComponent,
  refreshPlayerInChangeSource,
  isNextEpisode= false,
  isExitButton = true
}, context) {
  const PLAYER_ENVIRONMENT = options.environment || TBX_PLAYER_ENVS.get(window.__TBX_ENV__.NODE_ENV) || null;
  const [state, dispatch] = React.useReducer(reducer, {
    ...INITIAL_STATE,
    playerConfig: {
      ...options,
      pluginConfig: {
        titleOverlay: {
          translationTexts: translationTexts
        },
        concurrenceLimiter: {
          showAlert: false,
        },
        eventListenerAccion: !!eventListenerAccion,
        nextEpisode: {
          translationTexts: translationTexts
        },
        dvrseekbar: {
          translationTexts: translationTexts
        }
      },
      fullWindow: fullWindow,
      contentId: content?.id,
      ...(PLAYER_ENVIRONMENT ? { environment: PLAYER_ENVIRONMENT } : {})
    },
    content
  });

  const [backgroundVisible, setBackgroundVisible] = React.useState(true);
  const PLAYER_CONTAINER_REF = React.useRef();
  const EXIT_BUTTON_REF = React.useRef();

  const playerMarkup = `
  <video
    class="video-js vjs-default-skin"
    crossorigin="anonymous"
    id="${state.playerConfig.playerID}"
    preload="auto"
    controls
  />
`;

  //const thumbBackground = getContentImageByMode(content?.images, "THUMB")?.url;

  //#region /////// HOOKS ///////
  const _cleanupPlayer = hooks.useCleanupPlayer(state.player, dispatch, playerCallback)
  hooks.useTBXPlayerModule(
    state,
    PLAYER_CONTAINER_REF,
    dispatch,
    playerHandleError,
    playerCallback,
    _cleanupPlayer,
    isNextEpisode
  );
  hooks.useChangeSrc(state.player, dispatch, changeContent, translationTexts, playerHandleError, fullWindow, _cleanupPlayer)
  hooks.usePlayerCallbacks(state, context, dispatch, setBackgroundVisible, playerHandleError, onClickBackButton, enableGoBackOnCompletion, refreshPlayerInChangeSource, _cleanupPlayer, playerCallback);
  hooks.usePlayerLoadingErrorCallback(state.player, dispatch, _cleanupPlayer, playerHandleError);
  hooks.usePlayerCastCallbacks(state, context, dispatch, startCastingCallback);
  hooks.usePlayerPositionUpdatesCallbacks(state, context, updatePositionCallback);
  hooks.usePlayerExitButtomVideojsAnimation(state, EXIT_BUTTON_REF, playerCallback);
  //#endregion

  const LoaderResult = React.useCallback(() => {
    return (!!LoadingComponent ? <LoadingComponent className={'playerLoading'} /> : <LoadingSpinner className={'playerLoading'} />)
  }, []);

  return (
    <div className="playerContainerVideojs">
      {isExitButton && <Button id="playerExitButtomVideojsID" ref={EXIT_BUTTON_REF} className="playerExitButtomVideojs" onClick={() => onClickBackButton()}><i className="bi bi-arrow-left-short"></i></Button>}
      {
        backgroundVisible && <LoaderResult />
      }
      <div
        style={backgroundVisible ? { opacity: 0 } : { opacity: 1 }}
        data-vjs-player
        ref={el => { PLAYER_CONTAINER_REF.current = el; }}
        dangerouslySetInnerHTML={{ __html: playerMarkup }}
      />
    </div>
  );
};

Player.propTypes = {
  content: PropTypes.shape({
    description: PropTypes.string,
    id: PropTypes.string.isRequired,
    title: PropTypes.string
  }).isRequired,
  handleChangeSrc: PropTypes.string,
  options: PropTypes.shape({
    castAppId: PropTypes.string,
    environment: PropTypes.string,
    fill: PropTypes.bool,
    fluid: PropTypes.bool,
    jwtToken: PropTypes.string,
    playerID: PropTypes.string.isRequired,
    responsive: PropTypes.bool
  }),
  startCastingCallback: PropTypes.func,
  updatePositionCallback: PropTypes.func,
  onClickBackButton: PropTypes.func,
  translationTexts: PropTypes.object,
  playerHandleError: PropTypes.func,
  enableGoBackOnCompletion: PropTypes.bool,
  refreshPlayerInChangeSource: PropTypes.func,
  isNextEpisode: PropTypes.bool
};

export default WithErrorBoundary(Player);
